import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody } from "@material-tailwind/react";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import FormWrapper from "components/Form/FormWrapper";
import FormInput from "components/Form/FormInput";
import { TimeChart } from "components/Cards/TimeChart";
import {
  fetchSubscriptionCharts,
  ChartPayload,
  SubscriptionChartsRequest,
} from "api/charts";
import { useDeepCompareEffect } from "hooks/useDeepCompareEffect";

type Option = { value: string; label: string };

const dateRangeOptions: Option[] = [
  { value: "last30days", label: "Last 30 days" },
  { value: "last90days", label: "Last 90 days" },
  { value: "thisYear", label: "This Year" },
  { value: "lastYear", label: "Last Year" },
  { value: "last12months", label: "Last 12 Months" },
  { value: "allTime", label: "All Time" },
];

const intervalOptions: Option[] = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const groupByOptions: Option[] = [
  { value: "none", label: "None" },
  { value: "platform", label: "Payment Provider" },
  { value: "originationPlatform", label: "Origination Platform" },
  { value: "product", label: "Product" },
  { value: "businessName", label: "Business Type" },
];

type IForm = {
  dateRange: Option;
  interval: Option;
  groupBy: Option;
  stacked: boolean;
};

function rangeFor(value: string): { start: DateTime; end: DateTime } {
  const now = DateTime.utc();
  switch (value) {
    case "last30days":
      return { start: now.minus({ days: 30 }).startOf("day"), end: now };
    case "last90days":
      return { start: now.minus({ days: 90 }).startOf("day"), end: now };
    case "thisYear":
      return { start: now.startOf("year"), end: now };
    case "lastYear": {
      const ly = now.minus({ years: 1 });
      return { start: ly.startOf("year"), end: ly.endOf("year") };
    }
    case "last12months":
      return { start: now.minus({ months: 12 }).startOf("day"), end: now };
    case "allTime":
    default:
      return { start: DateTime.fromISO("2019-06-01T00:00:00Z"), end: now };
  }
}

const empty: ChartPayload = { categories: [], series: [] };

const STORAGE_KEY = "chartsFilters";

function loadSavedFilters(): IForm {
  const defaults: IForm = {
    dateRange: dateRangeOptions[4], // Last 12 Months
    interval: intervalOptions[1], // Monthly
    groupBy: groupByOptions[0], // None
    stacked: true,
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as {
      dateRange?: string;
      interval?: string;
      groupBy?: string;
      stacked?: boolean;
    };
    return {
      dateRange:
        dateRangeOptions.find((o) => o.value === saved.dateRange) ??
        defaults.dateRange,
      interval:
        intervalOptions.find((o) => o.value === saved.interval) ??
        defaults.interval,
      groupBy:
        groupByOptions.find((o) => o.value === saved.groupBy) ??
        defaults.groupBy,
      stacked: saved.stacked ?? defaults.stacked,
    };
  } catch {
    return defaults;
  }
}

function persistFilters(form: IForm): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        dateRange: form.dateRange.value,
        interval: form.interval.value,
        groupBy: form.groupBy.value,
        stacked: form.stacked,
      }),
    );
  } catch {
    // Storage full or unavailable — silently ignore.
  }
}

const ChartsPage = () => {
  const [data, setData] = useState({
    trialsStarted: empty,
    activeSubscriptions: empty,
    revenue: empty,
    mrr: empty,
    churn: empty,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formMethods = useForm<IForm>({
    defaultValues: loadSavedFilters(),
  });

  const dateRange = formMethods.watch("dateRange");
  const interval = formMethods.watch("interval");
  const groupBy = formMethods.watch("groupBy");
  const stacked = formMethods.watch("stacked");

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    const form = formMethods.getValues();
    persistFilters(form);
    const range = rangeFor(form.dateRange.value);
    try {
      const result = await fetchSubscriptionCharts({
        start: range.start.toISO()!,
        end: range.end.toISO()!,
        interval: form.interval.value as SubscriptionChartsRequest["interval"],
        groupBy: form.groupBy.value as SubscriptionChartsRequest["groupBy"],
      });
      setData(result);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to load charts");
    } finally {
      setIsLoading(false);
    }
  };

  useDeepCompareEffect(() => {
    fetchData();
  }, [dateRange, interval, groupBy]);

  useEffect(() => {
    persistFilters(formMethods.getValues());
  }, [stacked]);

  return (
    <div className="mb-6 mt-12 flex flex-col gap-y-6">
      <Card className="border border-blue-gray-100 shadow-sm">
        <CardBody>
          <FormWrapper
            className="flex w-full flex-wrap items-center justify-start gap-x-4 gap-y-2"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            formMethods={formMethods}
          >
            <FunnelIcon className="h-6 w-6 text-blue-gray-500" />
            <FormInput<IForm>
              name="dateRange"
              type="select"
              options={dateRangeOptions}
            />
            <FormInput<IForm>
              name="interval"
              type="select"
              options={intervalOptions}
            />
            <FormInput<IForm>
              name="groupBy"
              type="select"
              options={groupByOptions}
            />
            <FormInput<IForm> name="stacked" type="checkbox" label="Stacked" />
          </FormWrapper>
          {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
        </CardBody>
      </Card>

      <TimeChart
        title="Active Subscriptions"
        categories={data.activeSubscriptions.categories}
        series={data.activeSubscriptions.series}
        isLoading={isLoading}
        stacked={stacked}
        projectedCount={data.activeSubscriptions.projectedCount ?? 0}
      />

      <TimeChart
        title="Trials Started"
        categories={data.trialsStarted.categories}
        series={data.trialsStarted.series}
        isLoading={isLoading}
        stacked={stacked}
        projectedCount={data.trialsStarted.projectedCount ?? 0}
      />

      <TimeChart
        title="Revenue (USD)"
        categories={data.revenue.categories}
        series={data.revenue.series}
        isLoading={isLoading}
        format="currency"
        stacked={stacked}
        projectedCount={data.revenue.projectedCount ?? 0}
      />

      <TimeChart
        title="MRR (USD)"
        categories={data.mrr.categories}
        series={data.mrr.series}
        isLoading={isLoading}
        format="currency"
        stacked={stacked}
        projectedCount={data.mrr.projectedCount ?? 0}
      />

      <TimeChart
        title="Churn (% of active subscriptions)"
        categories={data.churn.categories}
        series={data.churn.series}
        isLoading={isLoading}
        format="percent"
        stacked={false}
        projectedCount={data.churn.projectedCount ?? 0}
      />
    </div>
  );
};

export default ChartsPage;
